/**
 * A history router in the ~60 lines this app needs. A real router library would add a
 * dependency and a second mental model for something this small: routes are path patterns
 * with `:params`, matching is ordered so static prefixes win, and `navigate` keeps the
 * browser's back button working by going through `pushState`.
 */

import { useCallback, useEffect, useState } from "react";
import type { AnchorHTMLAttributes, ReactNode } from "react";

export type Route = {
  /** `league.round` style name, or the raw path when nothing matched. */
  name: string;
  path: string;
  params: Record<string, string>;
};

/** Ordered: the first match wins, so `/admin` and `/login` are checked before `/:slug`. */
const ROUTES: { pattern: string; name: string }[] = [
  { pattern: "/", name: "index" },
  { pattern: "/login", name: "login" },
  { pattern: "/account/password", name: "password" },
  { pattern: "/admin", name: "admin.index" },
  { pattern: "/admin/new", name: "admin.new" },
  { pattern: "/admin/accounts", name: "admin.accounts" },
  { pattern: "/admin/:slug", name: "admin.league" },
  { pattern: "/admin/:slug/entry", name: "entry.pick" },
  { pattern: "/admin/:slug/entry/:roundId", name: "entry.round" },
  { pattern: "/:slug/standings", name: "league.standings" },
  { pattern: "/:slug/join", name: "league.join" },
  { pattern: "/:slug/rounds/:roundId", name: "league.round" },
  { pattern: "/:slug/shooters/:shooterId", name: "league.shooter" },
  { pattern: "/:slug", name: "league.view" },
];

function match(path: string): Route {
  for (const { pattern, name } of ROUTES) {
    const patternParts = pattern.split("/").filter(Boolean);
    const pathParts = path.split("/").filter(Boolean);
    if (patternParts.length !== pathParts.length) continue;

    const params: Record<string, string> = {};
    let matched = true;
    for (let i = 0; i < patternParts.length; i += 1) {
      const part = patternParts[i];
      if (part.startsWith(":")) params[part.slice(1)] = decodeURIComponent(pathParts[i]);
      else if (part !== pathParts[i]) {
        matched = false;
        break;
      }
    }
    if (matched) return { name, path, params };
  }
  return { name: "notfound", path, params: {} };
}

export function navigate(to: string, options: { replace?: boolean } = {}): void {
  if (`/${window.location.pathname}` === to) return;
  if (options.replace) window.history.replaceState({}, "", to);
  else window.history.pushState({}, "", to);
  window.dispatchEvent(new PopStateEvent("aimly:navigate"));
}

export function useRoute(): Route {
  const read = useCallback(
    () => match(window.location.pathname === "" ? "/" : window.location.pathname),
    [],
  );
  const [route, setRoute] = useState<Route>(read);

  useEffect(() => {
    const onChange = () => setRoute(read());
    window.addEventListener("popstate", onChange);
    window.addEventListener("aimly:navigate", onChange);
    return () => {
      window.removeEventListener("popstate", onChange);
      window.removeEventListener("aimly:navigate", onChange);
    };
  }, [read]);

  return route;
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & { to: string; children: ReactNode };

/** Internal navigation. Anything off-origin must be a plain <a>, not this. */
export function Link({ to, children, onClick, ...rest }: LinkProps) {
  return (
    <a
      href={to}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        onClick?.(event);
        navigate(to);
      }}
      {...rest}
    >
      {children}
    </a>
  );
}
